import { zodResolver } from "@hookform/resolvers/zod"
import { format } from "date-fns"
import type { LucideIcon } from "lucide-react"
import {
  ArrowUpCircleIcon,
  BanknoteIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  CircleIcon,
  CirclePlusIcon,
  CreditCardIcon,
  EyeIcon,
  FilterIcon,
  GiftIcon,
  LandmarkIcon,
  QrCodeIcon,
  ReceiptIcon,
  RotateCcwIcon,
  SearchIcon,
  StarIcon,
  Trash2Icon,
  WalletIcon,
  XCircleIcon,
} from "lucide-react"
import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { FilterChip } from "@/components/commons"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useAuth } from "@/contexts/auth"
import { FilterOrderStatusEnum } from "@/enums/orders"
import { SubscriptionStatusEnum } from "@/enums/subscriptions"
import { formatDocument } from "@/helpers/format"
import { useSubscriptionsSearchParams } from "@/hooks"
import { toast } from "@/hooks/use-toast"
import { hasExactIdentifier } from "@/lib/exact-search"
import { cn } from "@/lib/utils"
import type { FilterSubscriptionSchemaProps } from "@/schemas/filter-subscription"
import { filterSubscriptionSchema } from "@/schemas/filter-subscription"
import { useAdminMerchantPaymentMethods } from "@/services/admin/merchants"
import { usePaymentMethodCatalog } from "@/services/admin/payment-method-catalog"
import type { MerchantPaymentMethodName } from "@/types/admin-merchant"
import { DateHelper } from "@/utils"

import { AppDateRangePicker } from "../commons"

interface StatusOption {
  value: SubscriptionStatusEnum | "ALL"
  label: string
  icon: LucideIcon
}

interface PaymentMethodOption {
  value: MerchantPaymentMethodName | "ALL"
  label: string
  icon: LucideIcon
}

const PAYMENT_METHOD_ICONS: Record<string, LucideIcon> = {
  BALANCE: WalletIcon,
  CREDIT_CARD: CreditCardIcon,
  DEBIT_CARD: CreditCardIcon,
  LOYALTY_POINTS: StarIcon,
  GIFT_CARD_VTEX: GiftIcon,
  OPEN_FINANCE: LandmarkIcon,
  BANK_SLIP: ReceiptIcon,
  BOLEPIX: ReceiptIcon,
  DIRECT_DEBIT: LandmarkIcon,
  NUPAY: BanknoteIcon,
  PIX: QrCodeIcon,
  PIX_AUTO: QrCodeIcon,
  CREDIT_CARD_PRE_APPROVAL: CreditCardIcon,
  WALLET: WalletIcon,
}

export const SubscriptionsFilterForm = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { params } = useSubscriptionsSearchParams()
  const { authData } = useAuth()
  const merchantId = authData?.user?.merchantId ?? ""
  const { data: merchantPaymentMethods } =
    useAdminMerchantPaymentMethods(merchantId)
  const { data: paymentMethodCatalog } =
    usePaymentMethodCatalog()

  const statusOptions: StatusOption[] = [
    {
      value: FilterOrderStatusEnum.ALL,
      label: t("subscriptions.filters.allStatus"),
      icon: CircleIcon,
    },
    {
      value: SubscriptionStatusEnum.CREATED,
      label: t("subscriptions.status.CREATED"),
      icon: CirclePlusIcon,
    },
    {
      value: SubscriptionStatusEnum.UNDER_REVIEW,
      label: t("subscriptions.status.UNDER_REVIEW"),
      icon: EyeIcon,
    },
    {
      value: SubscriptionStatusEnum.PAST_DUE,
      label: t("subscriptions.status.PAST_DUE"),
      icon: XCircleIcon,
    },
    {
      value: SubscriptionStatusEnum.CANCELED,
      label: t("subscriptions.status.CANCELED"),
      icon: XCircleIcon,
    },
    {
      value: SubscriptionStatusEnum.EXPIRED,
      label: t("subscriptions.status.EXPIRED"),
      icon: XCircleIcon,
    },
    {
      value: SubscriptionStatusEnum.IN_PROGRESS,
      label: t("subscriptions.status.IN_PROGRESS"),
      icon: ArrowUpCircleIcon,
    },
    {
      value: SubscriptionStatusEnum.FINISHED,
      label: t("subscriptions.status.FINISHED"),
      icon: CheckCircleIcon,
    },
  ]

  const [statusSelected, setStatusSelected] = useState<
    StatusOption | undefined
  >(
    statusOptions.find(
      (status) => status.value === params?.status
    ) || statusOptions[0]
  )
  const paymentMethodOptions: PaymentMethodOption[] =
    useMemo(
      () => [
        {
          value: "ALL",
          label: t(
            "subscriptions.filters.allPaymentMethods"
          ),
          icon: CircleIcon,
        },
        ...(merchantPaymentMethods ?? []).map((method) => ({
          value: method.paymentMethod,
          label:
            paymentMethodCatalog?.find(
              (item) =>
                item.paymentMethod === method.paymentMethod
            )?.description ?? method.paymentMethod,
          icon:
            PAYMENT_METHOD_ICONS[method.paymentMethod] ??
            WalletIcon,
        })),
      ],
      [t, merchantPaymentMethods, paymentMethodCatalog]
    )
  const [paymentMethodSelected, setPaymentMethodSelected] =
    useState<PaymentMethodOption | undefined>(
      paymentMethodOptions.find(
        (pm) => pm.value === params?.paymentMethod
      ) || paymentMethodOptions[0]
    )
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const defaultValues = {
    customerDocument: params?.customerDocument ?? "",
    code: params?.code ?? "",
    includePeriodWithIdentifier: false,
    status: params?.status,
    paymentMethod: params?.paymentMethod,
    from: new Date(
      DateHelper.formatToISO(
        params.from ?? DateHelper.currentDate()
      )
    ),
    to: new Date(
      DateHelper.formatToISO(
        params.to ?? DateHelper.currentDate()
      )
    ),
  }
  const subscriptionSchema = useMemo(
    () => filterSubscriptionSchema(t),
    [t]
  )
  const form = useForm<FilterSubscriptionSchemaProps>({
    resolver: zodResolver(subscriptionSchema),
    defaultValues: {
      ...defaultValues,
      status: statusSelected?.value,
      paymentMethod: paymentMethodSelected?.value,
    },
  })
  const watched = form.watch()
  const includesPeriodWithIdentifier =
    watched.includePeriodWithIdentifier === true

  /** O que está escolhido, filtro a filtro — é o que cada chip mostra fechado. */
  const activeFilters = useMemo(() => {
    const today = DateHelper.currentDate()
    const filters: { key: string; value: string }[] = []

    if (watched.customerDocument)
      filters.push({
        key: "customerDocument",
        value: watched.customerDocument,
      })
    if (watched.code)
      filters.push({ key: "code", value: watched.code })
    if (watched.status && watched.status !== "ALL")
      filters.push({
        key: "status",
        value: statusSelected?.label ?? watched.status,
      })
    if (
      watched.paymentMethod &&
      watched.paymentMethod !== "ALL"
    )
      filters.push({
        key: "paymentMethod",
        value:
          paymentMethodSelected?.label ??
          watched.paymentMethod,
      })

    const isDefaultRange = [
      watched.from,
      watched.to,
    ].every(
      (date) =>
        date &&
        format(date as Date, "yyyy-MM-dd") === today
    )
    if (watched.from && watched.to && !isDefaultRange)
      filters.push({
        key: "dateRange",
        value: `${format(
          watched.from as Date,
          "dd/MM/yy"
        )} – ${format(watched.to as Date, "dd/MM/yy")}`,
      })

    return filters
  }, [watched, statusSelected, paymentMethodSelected])

  const valueOf = (key: string) =>
    activeFilters.find((filter) => filter.key === key)?.value

  /** O chip de data nunca fica mudo: sem recorte próprio, mostra o padrão da tela. */
  const dateChipValue =
    valueOf("dateRange") ?? t("orders.filters.today")

  const clearFilter = (key: string) => {
    const today = new Date()
    if (key === "dateRange") {
      form.setValue("from", today)
      form.setValue("to", today)
      return
    }
    if (key === "status") {
      form.setValue("status", "ALL")
      setStatusSelected(statusOptions[0])
      return
    }
    if (key === "paymentMethod") {
      form.setValue("paymentMethod", "ALL")
      setPaymentMethodSelected(paymentMethodOptions[0])
      return
    }
    form.setValue(key as "code" | "customerDocument", "")
  }

  const hasFilter = useMemo(() => {
    return (
      params.code ||
      params.customerDocument ||
      params.status ||
      params.paymentMethod
    )
  }, [params])
  const handleSubmit = form.handleSubmit((data) => {
    // Mesma regra da lista de pedidos: com CPF, código ou identificador próprio o período sai da
    // consulta. A assinatura procurada pode ter começado em qualquer mês.
    // O período sai da consulta na busca por identificador — a menos que tenha sido pedido.
    const appliesPeriod =
      !hasExactIdentifier(data) ||
      data.includePeriodWithIdentifier

    if (appliesPeriod && data.from > data.to) {
      toast({
        title: t("common.invalidDateRange"),
      })
      return
    }
    const params = new URLSearchParams()
    Object.entries(data).forEach(([key, value]) => {
      // Opção da tela, não filtro: mandá-la ao backend criaria um parâmetro que ninguém lê.
      if (key === "includePeriodWithIdentifier") return
      if (value) {
        if (value === "ALL") return
        if (value instanceof Date) {
          if (!appliesPeriod) return
          value = format(value, "yyyy-MM-dd")
        }
        params.set(key, String(value))
      }
      params.set("refetch", "true")
    })
    navigate(`?${params.toString()}`)
  })
  const handleResetForm = () => {
    const currentDate = new Date()
    form.reset({
      code: "",
      customerDocument: "",
      status: "ALL",
      paymentMethod: "ALL",
      from: currentDate,
      to: currentDate,
      ownId: "",
      includePeriodWithIdentifier: false,
    })
    setStatusSelected(statusOptions[0])
    setPaymentMethodSelected(paymentMethodOptions[0])
    const params = new URLSearchParams()
    Object.entries({ params }).forEach(([key]) => {
      if (params.get(key)) {
        params.delete(key)
      }
    })
    navigate(`?${params.toString()}`)
  }
  // eslint-disable-next-line react-hooks/incompatible-library
  const selectedToDate = form.watch("to")
  // Identificador exato dispensa o período — deixá-lo editável e preenchido faria parecer filtro
  // ativo. O campo se desliga e diz o porquê.
  const searchesByIdentifier = hasExactIdentifier({
    customerDocument: form.watch("customerDocument"),
    code: form.watch("code"),
  })
  return (
    <div className="@container">
      {/* Mobile filter toggle. */}
      <button
        type="button"
        onClick={() => setIsFilterOpen((prev) => !prev)}
        className="mb-4 flex w-full items-center justify-between rounded-md border bg-background px-4 py-3 text-sm font-medium md:hidden"
      >
        <span className="flex items-center gap-2">
          <FilterIcon size={16} />
          {t("common.filters")}
          {hasFilter && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
              !
            </span>
          )}
        </span>
        <ChevronDownIcon
          size={16}
          className={cn(
            "transition-transform duration-200",
            isFilterOpen && "rotate-180"
          )}
        />
      </button>

      <div
        className={cn(
          "md:block",
          isFilterOpen ? "block" : "hidden"
        )}
      >
        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className="max-w-6xl"
          >
            <div className="flex flex-wrap items-center gap-1.5">
              <FilterChip
                label={t("orders.filters.createdPeriod")}
                value={dateChipValue}
                canClear={Boolean(valueOf("dateRange"))}
                onClear={() => clearFilter("dateRange")}
              >
                <FormField
                  control={form.control}
                  name="from"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <AppDateRangePicker
                          value={{
                            from: field.value,
                            to: selectedToDate,
                          }}
                          onChange={(range) => {
                            field.onChange(range.from)
                            form.setValue("to", range.to)
                          }}
                          disabled={
                            searchesByIdentifier &&
                            !includesPeriodWithIdentifier
                          }
                        />
                      </FormControl>
                      {searchesByIdentifier && (
                        // Mesma saída da lista de pedidos: em vez de só avisar que o período
                        // não vale, dá para pedir que valha.
                        <label className="flex items-start gap-2 pt-1 text-xs text-muted-foreground">
                          <Checkbox
                            checked={includesPeriodWithIdentifier}
                            onCheckedChange={(checked) =>
                              form.setValue(
                                "includePeriodWithIdentifier",
                                checked === true
                              )
                            }
                            className="mt-0.5"
                          />
                          <span>
                            {t("orders.filters.includePeriod")}
                          </span>
                        </label>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FilterChip>

              <FilterChip
                label={t("orders.filters.chips.customerDocument")}
                value={valueOf("customerDocument")}
                onClear={() => clearFilter("customerDocument")}
              >
                <FormField
                  control={form.control}
                  name="customerDocument"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <Input
                          placeholder={t(
                            "subscriptions.filters.customerDocument"
                          )}
                          maxLength={18}
                          {...field}
                          className="w-full"
                          onChange={(e) => {
                            const masked = formatDocument(
                              e.target.value
                            )
                            field.onChange(masked)
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </FilterChip>

              <FilterChip
                label={t("orders.filters.chips.code")}
                value={valueOf("code")}
                onClear={() => clearFilter("code")}
              >
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <Input
                          placeholder={t(
                            "subscriptions.filters.code"
                          )}
                          {...field}
                          className="w-full"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </FilterChip>

              <FilterChip
                label={t("orders.filters.chips.status")}
                value={valueOf("status")}
                onClear={() => clearFilter("status")}
              >
                <FormField
                  control={form.control}
                  name="status"
                  defaultValue={params?.status}
                  render={({ field }) => (
                    <FormItem>
                      <Popover>
                        <FormControl>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="h-10 w-full shrink-0 justify-start"
                            >
                              {field.value &&
                              statusSelected ? (
                                <>
                                  <statusSelected.icon className="mr-2 h-4 w-4" />
                                  {statusSelected?.label}
                                </>
                              ) : (
                                <>
                                  {t(
                                    "subscriptions.filters.selectStatus"
                                  )}
                                </>
                              )}
                            </Button>
                          </PopoverTrigger>
                        </FormControl>
                        <PopoverContent
                          className="w-[var(--radix-popover-trigger-width)] p-0"
                          align="start"
                        >
                          <Command>
                            <CommandInput
                              placeholder={t(
                                "subscriptions.filters.searchStatus"
                              )}
                            />
                            <CommandList>
                              <CommandEmpty>
                                {t(
                                  "subscriptions.filters.noStatusFound"
                                )}
                              </CommandEmpty>
                              <CommandGroup>
                                {statusOptions.map(
                                  (status) => (
                                    <CommandItem
                                      key={status.label}
                                      value={status.label}
                                      onSelect={() => {
                                        form.setValue(
                                          "status",
                                          status.value
                                        )
                                        setStatusSelected(
                                          status
                                        )
                                      }}
                                    >
                                      <status.icon
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          status.value ===
                                            field?.value
                                            ? "opacity-100"
                                            : "opacity-40"
                                        )}
                                      />
                                      <span>
                                        {status.label}
                                      </span>
                                    </CommandItem>
                                  )
                                )}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </FormItem>
                  )}
                />
              </FilterChip>

              <FilterChip
                label={t("orders.filters.chips.paymentMethod")}
                value={valueOf("paymentMethod")}
                onClear={() => clearFilter("paymentMethod")}
              >
                <FormField
                  control={form.control}
                  name="paymentMethod"
                  render={({ field }) => (
                    <FormItem>
                      <Popover>
                        <FormControl>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="h-10 w-full shrink-0 justify-start"
                            >
                              {field.value &&
                              paymentMethodSelected ? (
                                <>
                                  <paymentMethodSelected.icon className="mr-2 h-4 w-4" />
                                  {
                                    paymentMethodSelected?.label
                                  }
                                </>
                              ) : (
                                <>
                                  {t(
                                    "subscriptions.filters.selectPaymentMethod"
                                  )}
                                </>
                              )}
                            </Button>
                          </PopoverTrigger>
                        </FormControl>
                        <PopoverContent
                          className="w-[var(--radix-popover-trigger-width)] p-0"
                          align="start"
                        >
                          <Command>
                            <CommandInput
                              placeholder={t(
                                "subscriptions.filters.searchPaymentMethod"
                              )}
                            />
                            <CommandList>
                              <CommandEmpty>
                                {t(
                                  "subscriptions.filters.noPaymentMethodFound"
                                )}
                              </CommandEmpty>
                              <CommandGroup>
                                {paymentMethodOptions.map(
                                  (option) => (
                                    <CommandItem
                                      key={option.label}
                                      value={option.label}
                                      onSelect={() => {
                                        form.setValue(
                                          "paymentMethod",
                                          option.value
                                        )
                                        setPaymentMethodSelected(
                                          option
                                        )
                                      }}
                                    >
                                      <option.icon
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          option.value ===
                                            field?.value
                                            ? "opacity-100"
                                            : "opacity-40"
                                        )}
                                      />
                                      <span>
                                        {option.label}
                                      </span>
                                    </CommandItem>
                                  )
                                )}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </FormItem>
                  )}
                />
              </FilterChip>

              <Button
                size="sm"
                className="btn-whitelabel ml-1"
              >
                <SearchIcon size={16} className="mr-1.5" />
                {t("common.search")}
              </Button>

              {activeFilters.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleResetForm}
                  className="text-muted-foreground"
                >
                  <RotateCcwIcon size={14} className="mr-1.5" />
                  {t("orders.filters.clearAll")}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}
